import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { StyledTooltip } from '../utils/TableStyles';

/**
 * Selector de columnas para las grillas de React/MUI.
 *
 * Es el equivalente del botón `colvis` de DataTables Buttons, que las 37 grillas legacy
 * traen de fábrica y las de MUI no (ver docs/design-system/grid-column-selector.md). Va en
 * el toolbar de la grilla, junto al reset y al export.
 *
 * SE APOYA A PROPÓSITO EN EL CSS DEL colvis (`vec-datatables.css`): emite la misma
 * estructura de clases que arma DataTables —`.dt-button-collection > .dropdown-menu` con
 * items `.dropdown-item.buttons-columnVisibility`, y `.active` cuando la columna se ve— así
 * el panel es idéntico al de las grillas legacy sin duplicar el estilo. El acoplamiento es
 * deliberado: el requisito es que las dos arquitecturas se vean igual, y con una sola
 * definición se mantienen iguales solas. Lo único propio es el posicionamiento, porque acá
 * no hay un DataTables que ubique el panel (ver la regla `.dt-button-collection--react`).
 *
 * El estado no vive acá: lo maneja `useColumnVisibility`, para que la grilla pueda usar la
 * visibilidad en sus celdas y en su reset sin depender de que este botón esté montado.
 *
 * @param columns   columnas ofrecidas: `[{ name, messageId, label }]` (las `seleccionables`
 *                  del hook: ya vienen sin las gateadas por config ni las fijas).
 * @param esVisible (name) => bool
 * @param onToggle  (name) => void
 */
const GridColumnSelector = (props) => {
	const intl = useIntl();
	const [abierto, setAbierto] = useState(false);
	const [altoMaximo, setAltoMaximo] = useState(null);
	const contenedor = useRef(null);
	const panel = useRef(null);

	// El panel se acota al espacio que queda entre su borde superior y el de la pantalla, no a
	// una fracción fija del viewport: el toolbar puede estar a cualquier altura de la página y
	// las grillas nuevas tienen bastantes columnas (tickets ofrece 24, y sin tope el panel se
	// pasaba 129px del borde inferior).
	//
	// Se mide el panel ya montado, en el frame siguiente. El CSS deja un tope de respaldo
	// (60vh) para cuando el alto medido queda desactualizado: si otra parte de la pantalla
	// termina de cargar después y corre el toolbar —las tarjetas de KPI de tickets llegan por
	// AJAX y lo empujan ~135px—, el panel abierto no se vuelve a medir hasta la próxima vez.
	useEffect(() => {
		if (!abierto) return undefined;

		const cuadro = window.requestAnimationFrame(() => {
			if (!panel.current) return;
			const arriba = panel.current.getBoundingClientRect().top;
			setAltoMaximo(Math.max(200, window.innerHeight - arriba - 16));
		});
		return () => window.cancelAnimationFrame(cuadro);
	}, [abierto]);

	// Un click fuera cierra el panel, como hace el popover de DataTables Buttons. El
	// listener se registra solo mientras está abierto: no tiene sentido pagarlo siempre.
	useEffect(() => {
		if (!abierto) return undefined;

		const alClickearAfuera = (e) => {
			if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
		};
		document.addEventListener('mousedown', alClickearAfuera);
		return () => document.removeEventListener('mousedown', alClickearAfuera);
	}, [abierto]);

	const alternar = () => {
		if (abierto) setAltoMaximo(null);
		setAbierto(!abierto);
	};

	const columnas = props.columns || [];
	if (columnas.length === 0) return null;

	const etiqueta = (columna) => {
		if (columna.messageId) {
			return intl.formatMessage({ id: columna.messageId, defaultMessage: columna.label || columna.name });
		}
		return columna.label || columna.name;
	};

	const titulo = intl.formatMessage({
		id: 'grid.tools_select_columns.title_seleccionar_columnas',
		defaultMessage: 'Seleccionar columnas',
	});

	return (
		<div className="dt-button-selector-react" ref={contenedor} style={{ position: 'relative', display: 'inline-block' }}>
			<StyledTooltip title={titulo} arrow placement="top">
				<button
					type="button"
					className="btn btn-secondary buttons-collection"
					onClick={alternar}
					// El botón es solo un icono: sin esto queda sin nombre para un lector de
					// pantalla (el title del tooltip de MUI no alcanza, solo se anuncia mientras
					// está abierto).
					aria-label={titulo}
					aria-haspopup="true"
					aria-expanded={abierto}
				>
					<span>
						<i className="la la-columns"></i>
					</span>
				</button>
			</StyledTooltip>

			{abierto && (
				<div className="dt-button-collection dt-button-collection--react">
					<div className="dropdown-menu" role="menu" ref={panel} style={altoMaximo ? { maxHeight: altoMaximo } : undefined}>
						{columnas.map((columna) => (
							<button
								key={columna.name}
								type="button"
								// `active` = columna visible. Es la misma convención que usa DataTables
								// para pintar la casilla marcada, y de ahí sale el estilo.
								className={'dropdown-item buttons-columnVisibility' + (props.esVisible(columna.name) ? ' active' : '')}
								// La casilla es un ::before de CSS: sin esto, un lector de pantalla lee
								// el nombre de la columna pero no si está marcada.
								role="menuitemcheckbox"
								aria-checked={props.esVisible(columna.name)}
								onClick={() => props.onToggle(columna.name)}
							>
								{etiqueta(columna)}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default GridColumnSelector;
